import { useState, SetStateAction, Dispatch, ChangeEvent, CSSProperties } from 'react';
import TableImage from "assets/images/placeholders/tableImage.png";
import SwitchSymphony from "elements/SwitchSymphony";
import useScreen from "Hooks/useScreen";
import ButtonSymphony from "elements/ButtonSymphony";
import Sty, { StyledLabelP } from "modules/Sty";
import useUploadEmailsByCsv from "Hooks/useUploadEmailsByCsv";
import CardSymphony from 'elements/CardSymphony';
import styled from 'styled-components';
import FileChooser from 'elements/FileChooser';

interface UploadFileTabProps {
    isConsentConfirmed?: boolean;
    setIsConsentConfirmed: Dispatch<SetStateAction<boolean>>;
}

// NOTE: component()
const UploadFileTab = ({
    isConsentConfirmed,
    setIsConsentConfirmed
}: UploadFileTabProps) => {
    const { mobileView } = useScreen();
    const {
        upload,
        saveUpload,
        uploadCsv,
        isLoadingUploadEmails
    } = useUploadEmailsByCsv();

    const uploadInputMaxWidth = mobileView ? "380px" : "100%";

    const [selectedCsv, setSelectedCsv] = useState<File | undefined>(undefined);
    const [emailsFound, setEmailsFound] = useState(0);
    const [errorFileUpload, setErrorFileUpload] = useState(false);

    const handleDeleteCsv = () => {
        saveUpload({ ...upload, csv: undefined });
        setSelectedCsv(undefined);
    };

    const handleUploadCsv = async (file: File) => {
        const csv = file;
        const formData = new FormData();

        if (errorFileUpload) {
            setErrorFileUpload(false)
        }

        if (!csv) {
            setErrorFileUpload(true);
            return;
        }

        formData.append("csv", csv);

        try {
            const res = await uploadCsv(formData);
            const { data, error } = res;
            if (error) {
                setErrorFileUpload(true);
                return
            }

            setEmailsFound(data as number);
            saveUpload({ csv, totalEmails: data });
            setSelectedCsv(csv);
        } catch (err) {
            setErrorFileUpload(true);
            console.error('Error uploading CSV:', err);
        }
    };

    const renderErrorLabel = () => {
        return (
            <StyledLabelP fontSize={12} color="#707070" margin="8px 0px 0px 0px">
                An error occurred. Try again later.
            </StyledLabelP>
        )
    }

    const renderFoundEmailsLabel = (emailsFound: number) => {
        return (
            <StyledLabelP fontSize={12} color="#707070" margin="8px 0px 0px 0px">
                {emailsFound} emails found
            </StyledLabelP>
        )
    }

    const renderTableImage = () => {
        return (
            <img
                className="uploadFileTableImage"
                style={sty.uploadFileTableImage}
                src={TableImage}
                alt="tableImage.png"
            />
        )
    }

    // NOTE: render()
    return (
        <div
            className="uploadFileTabComponent"
            style={sty.uploadFileTabComponent}
        >
            <CardSymphony
                className="uploadFileUpperHeader"
                title="Upload your file"
                subtitle="Import contacts from a csv file, just be sure your file has at least these columns:"
                size="small"
            >
                <ul className="uploadFileUpperHeaderList" style={sty.unorderedList}>
                    <ListItem>Names</ListItem>
                    <ListItem>Email</ListItem>
                    <ListItem>City</ListItem>
                </ul>
                <FileChooser
                    className="uploadFansCsvButton"
                    onFileUpload={handleUploadCsv}
                    label="Upload CSV File"
                    isLoading={isLoadingUploadEmails}
                />

                {errorFileUpload && renderErrorLabel()}
                {!errorFileUpload && selectedCsv && renderFoundEmailsLabel(emailsFound)}

                {selectedCsv &&
                    <div
                        className="uploadFileLower"
                        style={sty.uploadFileLower}
                    >
                        <div className="uploadFileLowerSwitchContainer">
                            <SwitchSymphony
                                className="uploadFileLowerSwitch"
                                checked={isConsentConfirmed}
                                onChange={() => setIsConsentConfirmed(!isConsentConfirmed)}
                                color="purple"
                            />
                        </div>
                        <div className="uploadFileLowerInfoContainer">
                            <CardSymphony
                                className="uploadFileLowerInfoContainerTitle"
                                title="Consent Confirmation"
                                subtitle="Confirm that these users have given consent to be contacted by your brand."
                                isNoPadding
                                size="small"
                            />
                            <ButtonSymphony
                                className="uploadFileLowerInfoContainerLearnMore"
                                variant="text"
                                onClick={() => { }}
                            >
                                Learn more
                            </ButtonSymphony>
                        </div>
                    </div>
                }
            </CardSymphony>

            {renderTableImage()}
        </div>
    );
};

const ListItem = styled.li`
  padding-left: 16px;
  color: #707070;
  &::before {
    content: "\u2022";
    color: #707070;
    font-weight: bold;
    display: inline-block;
    width: 1em;
    margin-left: -1em;
  }
`;

const sty: Record<string, CSSProperties> = {
    uploadFileTabComponent: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        borderRadius: 16,
        gap: 16
    },
    unorderedList: {
        listStyle: 'none'
    },
    uploadFileLower: {
        borderTop: Sty.border,
        marginTop: 16,
        paddingTop: 24,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'left',
        alignItems: 'center',
        gap: 16
    },
    uploadFileTableImage: {
        borderRadius: 16,
        width: '100%',
        alignSelf: 'flex-start'
    }
};

export default UploadFileTab;
